import Component from '@glimmer/component';
import Avatar from './avatar';
import type { AvatarSize } from './avatar';

// ---------------------------------------------------------------------------
// AvatarGroup – overlapping stack of avatars
// ---------------------------------------------------------------------------

/**
 * A single person entry for the group.
 */
export interface AvatarGroupPerson {
  /** Full name for initials and color derivation */
  name: string;
  /** Optional image URL */
  src?: string;
  /** Optional background color override */
  color?: string;
}

interface AvatarGroupSignature {
  Args: {
    /** Array of people to display */
    people: AvatarGroupPerson[];
    /** Avatar size @default 'sm' */
    size?: AvatarSize;
    /** Maximum visible avatars before showing "+N" overflow @default 5 */
    max?: number;
    /** Overlap spacing — higher = more overlap. @default 'normal' */
    spacing?: 'tight' | 'normal' | 'loose';
  };
  Element: HTMLDivElement;
}

const OVERLAP_CLASSES: Record<string, string> = {
  tight: '-ml-2',
  normal: '-ml-2.5',
  loose: '-ml-3',
};

const OVERFLOW_SIZE_CLASSES: Record<AvatarSize, string> = {
  xs: 'w-5 h-5 text-[9px]',
  sm: 'w-7 h-7 text-[10px]',
  md: 'w-9 h-9 text-xs',
  lg: 'w-11 h-11 text-sm',
  xl: 'w-14 h-14 text-sm',
};

/**
 * AvatarGroup – displays an overlapping row of avatars with optional "+N" overflow.
 *
 * Fixes the "too tight" overlap from the hand-rolled whos-viewing approach by using
 * consistent negative margins and proper ring separation.
 *
 * @example Basic
 * ```gts
 * import AvatarGroup from 'tio-ui/components/avatar-group';
 *
 * <AvatarGroup @people={{this.viewers}} @size="sm" @max={{4}} />
 * ```
 *
 * @example Loose spacing
 * ```gts
 * <AvatarGroup @people={{this.team}} @spacing="loose" @size="md" />
 * ```
 */
export default class AvatarGroup extends Component<AvatarGroupSignature> {
  get size(): AvatarSize {
    return this.args.size ?? 'sm';
  }

  get max() {
    const raw = this.args.max;
    if (raw == null) return 5;
    if (!Number.isFinite(raw)) return 5;
    return Math.max(1, Math.floor(raw));
  }

  get spacing() {
    return this.args.spacing ?? 'normal';
  }

  get overlapClass() {
    return OVERLAP_CLASSES[this.spacing] ?? OVERLAP_CLASSES['normal'];
  }

  get overflowSizeClass() {
    return OVERFLOW_SIZE_CLASSES[this.size];
  }

  get visiblePeople() {
    return this.args.people.slice(0, this.max);
  }

  get hasOverflow() {
    return this.args.people.length > this.max;
  }

  get overflowCount() {
    return this.args.people.length - this.max;
  }

  <template>
    <div data-test-avatar-group class="tio-inline-row" ...attributes>
      {{#each this.visiblePeople as |person idx|}}
        <Avatar
          @name={{person.name}}
          @src={{person.src}}
          @color={{person.color}}
          @size={{this.size}}
          class="{{if idx this.overlapClass}} ring-2 ring-white"
        />
      {{/each}}
      {{#if this.hasOverflow}}
        <span
          data-test-avatar-group-overflow
          class="inline-flex items-center justify-center rounded-full tio-bg-chip tio-fg-secondary font-semibold ring-2 ring-white
            {{this.overlapClass}}
            {{this.overflowSizeClass}}"
        >
          +{{this.overflowCount}}
        </span>
      {{/if}}
    </div>
  </template>
}

export type { AvatarGroupSignature };
